if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.variantIdInput.disabled = false;
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
        this.cart =
          document.querySelector("cart-notification") ||
          document.querySelector("cart-drawer");
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector("span");
        this.upsellVariantId = this.dataset.upsellVariantId;

        if (document.querySelector("cart-drawer"))
          this.submitButton.setAttribute("aria-haspopup", "dialog");

        this.hideErrors = this.dataset.hideErrors === "true";
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute("aria-disabled") === "true") return;

        this.handleErrorMessage();

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        this.querySelector(".loading__spinner").classList.remove("hidden");

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            "sections",
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append("sections_url", window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        this.skipCart = false;
        const variantInput = this.form.querySelector('[name=id]');
        if (variantInput?.dataset?.skipCart === 'true') {
          this.skipCart = true;
        }

        // Vellhaven: open the cart drawer right away, in a loading state, instead of
        // waiting for the add request to come back - a tap on "Add to cart" then feels
        // instant. The drawer's real contents replace it below (renderContents).
        const drawer =
          !this.skipCart &&
          this.cart &&
          this.cart.tagName === "CART-DRAWER" &&
          !this.closest("quick-add-modal") &&
          window.themeSettings?.cart?.auto_open_drawer !== false
            ? this.cart
            : null;
        // While loading, the old contents are dimmed and kept out of reach of taps and the
        // keyboard (inert); they are replaced or the drawer closes again when the add fails.
        const busyParts = () =>
          drawer ? drawer.querySelectorAll("cart-drawer-items, .drawer__footer") : [];
        if (drawer) {
          drawer.classList.add("vh-adding");
          drawer.querySelector(".drawer__inner")?.setAttribute("aria-busy", "true");
          busyParts().forEach((part) => (part.inert = true));
          drawer.open();
        }
        const stopLoading = (closeDrawer) => {
          if (!drawer) return;
          drawer.classList.remove("vh-adding");
          drawer.querySelector(".drawer__inner")?.removeAttribute("aria-busy");
          busyParts().forEach((part) => (part.inert = false));
          // nothing was added: close the drawer again (unless the shopper already did)
          if (closeDrawer && drawer.classList.contains("active")) drawer.close();
        };
        // Never swap the contents while the drawer is still sliding in (fast answers
        // would make the panel jump); the slide takes 200 ms.
        const slideDone = drawer
          ? new Promise((resolve) => setTimeout(resolve, 260))
          : Promise.resolve();
        let added = false;

        // Vellhaven: the cart is only read first when there is an upsell product to add
        // to an empty cart; for everyone else that extra round trip just delayed the add.
        const addUpsellFirst = this.upsellVariantId
          ? fetch(`${routes.cart_url}.js`)
              .then((response) => response.json())
              .then((cartState) => {
                if (cartState.item_count === 0) {
                  return fetch(
                    `${routes.cart_add_url}`,
                    this.getUpsellProductConfig()
                  );
                }
              })
          : Promise.resolve();

        addUpsellFirst
          .then(() => {
            return fetch(`${routes.cart_add_url}`, config);
          })

          .then((response) => response.json())
          .then((response) => slideDone.then(() => response))
          .then((response) => {
            if (response.status) {
              stopLoading(true);
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage =
                this.submitButton.querySelector(".sold-out-message");
              if (!soldOutMessage) return;
              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButtonText.classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              this.error = true;
              return;
            }
            added = true;
            if (this.skipCart) {
              window.location = "/checkout";
              return;
            } else if (!this.cart) {
              if (!this.error) {
                publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: "product-form",
                  productVariantId: formData.get("id"),
                  cartData: response,
                });
              }
              this.error = false;
              if (window.themeSettings?.cart?.auto_open_page) {
                window.location = window.routes.cart_url;
              } else if (response.sections?.["cart-icon-bubble"]) {
                const cartIconBubble = document.getElementById("cart-icon-bubble");
                if (cartIconBubble) {
                  cartIconBubble.innerHTML = new DOMParser()
                    .parseFromString(response.sections["cart-icon-bubble"], "text/html")
                    .querySelector(".shopify-section").innerHTML;
                }
              }
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: formData.get("id"),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest("quick-add-modal");
            if (quickAddModal) {
              document.body.addEventListener(
                "modalClosed",
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
            stopLoading(true);
          })
          .finally(() => {
            stopLoading(false);
            this.submitButton.classList.remove("loading");
            // only a successful add makes the cart non-empty (a failed add used to leave
            // an empty drawer without its "Your cart is empty" text)
            if (added && this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            if (!this.error) this.submitButton.removeAttribute("aria-disabled");
            this.querySelector(".loading__spinner").classList.add("hidden");
          });
      }

      getUpsellProductConfig() {
        const upsellFormData = new FormData();
        upsellFormData.append("id", this.upsellVariantId);
        upsellFormData.append("quantity", 1);

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];
        config.body = upsellFormData;

        return config;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message"
          );

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute("disabled", "disabled");
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute("disabled");
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector("[name=id]");
      }
    }
  );
}
